import { useState, useEffect, useRef } from "react";

import { useDidMountEffect } from "../../utils/utils";
import { adminEndpoints } from "../../api/endpoints";

export const stateOptions = [
  { value: 0, label: "Aufgesetzt", filter: { assigned: false } },
  {
    value: 1,
    label: "Zugeordnet",
    filter: { assigned: true, configured: false },
  },
  {
    value: 2,
    label: "Konfiguriert",
    filter: { assigned: true, configured: true },
  },
];

export const getClientState = (client) => {
  if (!client.location) return stateOptions.find((o) => o.value === 0);
  else if (!client.configured) return stateOptions.find((o) => o.value === 1);
  return stateOptions.find((o) => o.value === 2);
};

export default function useClients(
  apiClient,
  showMsg = null,
  isFocused = true,
  initialState = null,
  nested = false,
) {
  const [clients, setClients] = useState(null);

  // pagination
  const [batch, setBatch] = useState(0);
  const [nPages, setNPages] = useState(null);
  const batchSize = 20;
  const lastBatch = useRef(null);

  // search filter
  const [searchInput, setSearchInput] = useState(null);

  // order
  const [orderBy, setOrderBy] = useState("-first_query_timestamp");

  // columns filtering
  const [onlineFilter, setOnlineFilter] = useState(null);
  const [beenOnlineFilter, setBeenOnlineFilter] = useState(true);
  const [stateFilter, setStateFilter] = useState(initialState);
  const [userFilter, setUserFilter] = useState(null);

  const loadData = () => {
    getClients();
  };

  const initData = () => {
    if (batch === 0) getClients();
    else setBatch(0);
  };

  const extendData = () => addClients();

  const clearData = () => {
    setNPages(null);
    setClients(null);
  };

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  useEffect(() => {
    if (!clients) return; // run if 'searchInput' changes, but not on initial render
    const delayDebounceFn = setTimeout(() => {
      initData();
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useDidMountEffect(() => {
    initData();
  }, [orderBy, onlineFilter, beenOnlineFilter, stateFilter, userFilter]);

  useDidMountEffect(() => {
    // run if 'batch' changes, but not on initial render
    loadData();
  }, [batch]);

  const clientRequest = async (_batch) => {
    const stateOption =
      stateFilter == null
        ? null
        : stateOptions.find((o) => o.value === stateFilter);
    const params = {
      nested,
      batch_size: batchSize,
      batch: _batch,
      search: searchInput || null,
      ascending_ids: false,
      order_by: orderBy,
      online: onlineFilter,
      been_online: beenOnlineFilter,
      user_created: userFilter,
      ...(stateOption ? stateOption.filter : {}),
    };
    const response = await apiClient.get(adminEndpoints.client, params);
    return response;
  };

  const getClients = async () => {
    try {
      lastBatch.current = batch;
      const response = await clientRequest(batch);
      setNPages(Number(response.headers.length));
      setClients(response.data);
      return true;
    } catch (error) {
      console.warn('Error in "getClients"', error, error.stack);
      return false;
    }
  };

  const addClients = async () => {
    if (lastBatch.current === null) return 
    if (nPages !== null && lastBatch.current + 1 >= nPages) return true;
    try {
      lastBatch.current = lastBatch.current + 1;
      const response = await clientRequest(lastBatch.current || 0);
      setClients((cs) => [...cs, ...response.data]);
      return true;
    } catch (error) {
      console.warn('Error in "addClients"', error, error.stack);
      return false;
    }
  };

  const getOrderBy = (key) => {
    switch (orderBy) {
      case key:
        return true;
      case `-${key}`:
        return false;
      default:
        return null;
    }
  };

  const updateOrderBy = (key, ascending) => {
    setOrderBy(ascending === null ? null : ascending ? key : `-${key}`);
  };

  const resetDate = async (clientId, onSuccess) => {
    try {
      const response = await apiClient.put(adminEndpoints.client + clientId, {
        first_query_timestamp: null,
      });
      showMsg?.("Das Datum wurde zurückgesetzt");
      setClients((cs) =>
        cs.map((c) => (c.id === clientId ? { ...c, ...response.data } : c)),
      );
      onSuccess?.();
    } catch (error) {
      console.warn('Error in "client:onResetDate"', error, error.stack);
      showMsg?.("Das Datum konnte nicht zurückgesetzt werden", "error");
    }
  };

  return {
    resetDate,
    order: { get: getOrderBy, set: updateOrderBy },
    clients,
    data: {
      update: loadData,
      clear: clearData,
      init: initData,
      extend: extendData,
    },
    search: { input: searchInput, setInput: setSearchInput },
    pagination: { nPages, setPage: setBatch, page: batch },
    filter: {
      beenOnline: { value: beenOnlineFilter, setValue: setBeenOnlineFilter },
      online: { value: onlineFilter, setValue: setOnlineFilter },
      user: { value: userFilter, setValue: setUserFilter },
      state: {
        value: stateFilter,
        setValue: setStateFilter,
        options: stateOptions,
      },
    },
  };
}
